<template>
    <div class="video-player">
        <div
            v-if="error"
            class="capitalize"
        >
            {{ $t('video not available') }}
        </div>
        <div ref="betGeniusPlayer" />
        <div
            id="betgenius-player"
            class="video"
        />
    </div>
</template>

<script>
    import { getConfig } from '@superbet-group/web.lib.config';
    import {
        mapActions,
        mapGetters,
    } from 'vuex';
    import { sendMultimediaRequestResultAnalytic } from '@core/services/media/MediaService';

    import { errorGuard } from '@utils/ErrorGuard';
    import { deviceType } from '@src/app/utils/browser/deviceType';

    /**
     * Documentation: https://dap-docs.betstream.betgenius.com/how-to-integrate
     */
    export default {
        name: 'BetGeniusPlayer',
        props: {
            videoId: {
                type: String,
                required: true,
            },
            sourceId: {
                type: String,
                required: true,
            },
        },
        data() {
            return {
                error: false,
            };
        },
        mounted() {
            this.createPlayer();
            this.createListeners();
        },
        computed: {
            ...mapGetters('data/authentication', [
                'user',
            ]),
            ...mapGetters('ui/sportOffer', [
                'expandedEventData',
            ]),
        },
        methods: {
            ...mapActions('ui/sportOffer', [
                'getBetGenius',
            ]),
            reportError(error, additionalMessages = []) {
                sendMultimediaRequestResultAnalytic(
                    this.expandedEventData,
                    'false',
                    error,
                    additionalMessages,
                );
            },
            createPlayer() {
                const script = document.createElement('script');
                script.type = 'text/javascript';
                const qs = new URLSearchParams({
                    customerId: this.sourceId,
                    fixtureId: this.videoId,
                    containerId: 'betgenius-player',
                    width: '100%',
                    height: '100%',
                    bufferLength: 2,
                    allowFullScreen: false,
                    autoplayEnabled: true,
                });
                script.src = `//genius-live-player-production.betstream.betgenius.com/widgetLoader?${qs.toString()}`;
                script.onerror = (error) => this.reportError(new Error('Failed to load player script'), [error?.message]);

                this.$refs.betGeniusPlayer.appendChild(script);
            },
            async processBetGeniusEvent(event) {
                if (event.detail.type === 'player_ready') {
                    const {
                        deliveryType,
                        streamId,
                        deliveryId,
                    } = event.detail.body;

                    await errorGuard(async () => {
                        const result = await this.getBetGenius({
                            deliveryType,
                            streamId,
                            deliveryId,
                            fixtureId: this.videoId,
                            device: deviceType.toUpperCase(),
                            userId: this.user?.username || 'bg_anonymous',
                            region: getConfig().appLocale.locale,
                        }, this.expandedEventData);

                        if (result?.url && result?.token) {
                            window.GeniusLivePlayer.player.start(result);
                        } else {
                            this.error = true;
                        }
                    });
                } else if (event.detail.type === 'player_not_ready') {
                    const errorMessages = event.detail?.body?.error
                        ?.map((error) => error.message)
                        ?.filter(Boolean) ?? [];
                    this.reportError(new Error('Player not ready'), errorMessages);
                }
            },
            createListeners() {
                window.addEventListener('geniussportsmessagebus', this.processBetGeniusEvent);
            },
        },
        destroyed() {
            window.GeniusLivePlayer.player.close();

            window.removeEventListener('geniussportsmessagebus', this.processBetGeniusEvent);
        },
    };
</script>
